import useSWR from 'swr';
import { toast } from 'sonner';
import { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { AnexosInput } from 'src/components/anexos-input';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { anexarProtocolo } from 'src/actions/anexos';

// ----------------------------------------------------------------------
// Protocolo → Cadastrar Processo. Layout 2 colunas: form seccionado à esquerda
// (Dados do processo · Contato · Endereço) + resumo/ações FIXOS à direita — sem
// pilha de cards. Backend: GET /protocolo/assuntos + POST /protocolo (numeração
// <prefixo>-<ano>/<seq> pela app). CPF/CNPJ e nome do solicitante vêm do logado.
// ----------------------------------------------------------------------

interface Assunto {
  id: number;
  nome: string;
  /** Texto padrão do assunto — pré-preenche a descrição. */
  descricao: string | null;
  tipo: string | null;
  prefixo: string | null;
}

interface MeusDados {
  documento: string;
  nome: string | null;
  email?: string | null;
  telefone?: string | null;
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  municipio?: string | null;
  uf?: string | null;
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {titulo}
      </Typography>
      {children}
    </Box>
  );
}

export default function ProtocoloNovoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data } = useSWR<{ assuntos: Assunto[] }>('/portal-api/protocolo/assuntos', fetcher);
  const assuntos = data?.assuntos ?? [];
  // Autofill de contato/endereço do cadastro do contribuinte (pedido 20/jul).
  const { data: meusDados } = useSWR<MeusDados>('/portal-api/protocolo/meus-dados', fetcher);

  const [caId, setCaId] = useState<number | ''>('');
  const [descricao, setDescricao] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cep, setCep] = useState('');
  const [uf, setUf] = useState('');
  const [bairro, setBairro] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [anexos, setAnexos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);

  const assunto = assuntos.find((a) => a.id === caId);
  const anoAtual = new Date().getFullYear();

  // Preenche contato/endereço UMA vez quando o cadastro chega — sem sobrescrever
  // o que o contribuinte já digitou.
  const autofillFeito = useRef(false);
  useEffect(() => {
    if (!meusDados || autofillFeito.current) return;
    autofillFeito.current = true;
    if (meusDados.email) setEmail((v) => v || meusDados.email!);
    if (meusDados.telefone) setTelefone((v) => v || meusDados.telefone!);
    if (meusDados.cep) setCep((v) => v || meusDados.cep!);
    if (meusDados.uf) setUf((v) => v || meusDados.uf!);
    if (meusDados.bairro) setBairro((v) => v || meusDados.bairro!);
    if (meusDados.logradouro) setLogradouro((v) => v || meusDados.logradouro!);
    if (meusDados.numero) setNumero((v) => v || meusDados.numero!);
  }, [meusDados]);

  // ?assunto=N (CTA "Abrir protocolo" vindo da notificação DTE): pré-seleciona
  // o assunto assim que a lista chega.
  const assuntoParamAplicado = useRef(false);
  useEffect(() => {
    if (assuntoParamAplicado.current || !assuntos.length) return;
    const param = Number(searchParams.get('assunto'));
    if (Number.isInteger(param) && param > 0 && assuntos.some((a) => a.id === param)) {
      assuntoParamAplicado.current = true;
      setCaId(param);
      const alvo = assuntos.find((a) => a.id === param);
      if (alvo?.descricao) setDescricao((v) => v || alvo.descricao!);
    }
  }, [assuntos, searchParams]);

  // Texto padrão do assunto vira a descrição inicial — o contribuinte edita à
  // vontade; só preenche se a descrição está vazia.
  const trocarAssunto = (novoId: number | '') => {
    setCaId(novoId);
    if (novoId !== '') {
      const alvo = assuntos.find((a) => a.id === novoId);
      if (alvo?.descricao) setDescricao((v) => v || alvo.descricao!);
    }
  };

  const enviar = async () => {
    if (caId === '') {
      toast.error('Selecione o assunto.');
      return;
    }
    setEnviando(true);
    try {
      const res = await axiosInstance.post('/portal-api/protocolo', {
        ca_id: caId,
        cp_descricao: descricao || undefined,
        cp_email: email || undefined,
        cp_telefone: telefone || undefined,
        cp_cep: cep || undefined,
        cp_uf: uf || undefined,
        cp_bairro: bairro || undefined,
        cp_logradouro: logradouro || undefined,
        cp_numero: numero || undefined,
        cp_complemento: complemento || undefined,
      });
      const protocolo = res.data?.protocolo ?? '';
      toast.success(`Processo aberto: ${protocolo}`);

      // Anexa os arquivos ao processo recém-criado. Se algum falhar, o processo
      // já existe — o contribuinte reenvia pela tela de detalhe.
      if (protocolo && anexos.length) {
        const falhas = await anexarProtocolo(protocolo, anexos);
        if (falhas > 0) toast.error(`${falhas} anexo(s) não puderam ser enviados. Tente reenviá-los no processo.`);
      }

      navigate(protocolo ? `/protocolo/${encodeURIComponent(protocolo)}` : '/protocolo');
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Falha ao abrir o processo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading="Cadastrar Processo"
        links={[{ name: 'Protocolo', href: '/protocolo' }, { name: 'Cadastrar' }]}
        action={
          <Button color="inherit" startIcon={<Iconify icon="eva:arrow-ios-back-fill" />} onClick={() => navigate('/protocolo')}>
            Voltar
          </Button>
        }
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      <Grid container spacing={3}>
        {/* Form seccionado */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>
              <Secao titulo="Dados do processo">
                <Stack spacing={2.5}>
                  <TextField
                    select
                    fullWidth
                    label="Assunto"
                    value={caId}
                    onChange={(e) => trocarAssunto(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    {assuntos.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.nome}
                        {a.tipo ? ` · ${a.tipo}` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Descrição / solicitação"
                    placeholder="Descreva o que você precisa..."
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                  />
                </Stack>
              </Secao>

              <Divider />

              <Secao titulo="Contato">
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
                  </Grid>
                </Grid>
              </Secao>

              <Divider />

              <Secao titulo="Endereço">
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField fullWidth label="CEP" value={cep} onChange={(e) => setCep(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 7 }}>
                    <TextField fullWidth label="Logradouro" value={logradouro} onChange={(e) => setLogradouro(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField fullWidth label="Número" value={numero} onChange={(e) => setNumero(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Bairro" value={bairro} onChange={(e) => setBairro(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 2 }}>
                    <TextField fullWidth label="UF" value={uf} onChange={(e) => setUf(e.target.value)} inputProps={{ maxLength: 2 }} />
                  </Grid>
                  <Grid size={{ xs: 8, sm: 4 }}>
                    <TextField fullWidth label="Complemento" value={complemento} onChange={(e) => setComplemento(e.target.value)} />
                  </Grid>
                </Grid>
              </Secao>

              <Divider />

              <Secao titulo="Anexos">
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Anexe documentos ao processo (PDF, imagens, planilhas). Até 10 MB por arquivo.
                </Typography>
                <AnexosInput arquivos={anexos} onChange={setAnexos} disabled={enviando} />
              </Secao>
            </Stack>
          </Card>
        </Grid>

        {/* Resumo + ações fixos */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ p: 3, position: { md: 'sticky' }, top: { md: 88 } }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Resumo
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Assunto</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
                  {assunto?.nome ?? '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tipo</Typography>
                {assunto?.tipo ? <Label color="default">{assunto.tipo}</Label> : <Typography variant="body2">—</Typography>}
              </Box>
              {assunto?.prefixo && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Protocolo</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {assunto.prefixo}-{anoAtual}/…
                  </Typography>
                </Box>
              )}
              {anexos.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Anexos</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{anexos.length} arquivo(s)</Typography>
                </Box>
              )}
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 3 }}>
              <Button variant="contained" size="large" onClick={enviar} disabled={enviando || caId === ''}>
                Abrir processo
              </Button>
              <Button variant="outlined" color="inherit" onClick={() => navigate('/protocolo')}>
                Cancelar
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </DashboardContent>
  );
}
